import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchNewsById, fetchNews } from '../services/api';
import Container from '../components/layout/Container';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../hooks/useTranslation';

// News Card Component for Other News
const NewsCard = ({ item, formatDate, index, t }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    
    const handleCardClick = useCallback((e) => {
        if (e.target.tagName === 'A' || e.target.closest('a')) {
            return;
        }
        window.location.href = `/news/${item.id}`;
    }, [item.id]);

    return (
        <div 
            className="group bg-white/90 backdrop-blur-lg rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden border border-white/30 cursor-pointer h-full flex flex-col min-h-[400px]"
            style={{ 
                animationDelay: `${index * 0.1}s`,
                animation: 'fadeInUp 0.6s ease-out forwards'
            }}
            onClick={handleCardClick}
        >
            {item.image && (
                <div className="relative overflow-hidden h-48 flex-shrink-0">
                    {!imageLoaded && (
                        <div className="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                            <div className="w-6 h-6 border-2 border-slate-300 border-t-red-500 rounded-full animate-spin"></div>
                        </div>
                    )}
                    
                    <img 
                        src={item.image} 
                        alt={item.title}
                        className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ${
                            imageLoaded ? 'opacity-100' : 'opacity-0'
                        }`}
                        onLoad={() => setImageLoaded(true)}
                    />
                    
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    {item.is_featured && (
                        <div className="absolute top-3 left-3">
                            <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg backdrop-blur-sm">
                                ⭐ {t('pages.news.featured') || 'Asosiy'}
                            </span>
                        </div>
                    )}

                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-slate-700 text-xs font-semibold px-2 py-1 rounded-full shadow-md">
                        📖 2 daqiqa
                    </div>
                </div>
            )}

            <div className="p-5 space-y-4 flex flex-col flex-grow">
                <h3 className="text-base font-bold text-slate-900 leading-tight line-clamp-2 group-hover:text-red-600 transition-colors duration-300">
                    <Link to={`/news/${item.id}`} className="hover:text-red-600" onClick={(e) => e.stopPropagation()}>
                        {item.title || t('pages.news.noTitle') || 'Sarlavha yo\'q'}
                    </Link>
                </h3>

                <p className="text-slate-600 leading-relaxed line-clamp-3 text-sm flex-grow">
                    {item.content || item.summary || t('pages.news.noContent') || 'Matn yo\'q...'}
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-slate-100 mt-auto">
                    <div className="flex items-center space-x-2 text-slate-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs font-medium">
                            {formatDate(item.created_at)}
                        </span>
                    </div>
                    
                    <Link 
                        to={`/news/${item.id}`}
                        className="group/link inline-flex items-center text-red-600 hover:text-red-700 font-semibold text-sm transition-all duration-200 bg-red-50 hover:bg-red-100 px-3 py-1 rounded-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <span className="mr-1 text-xs">{t('common.readMore') || 'Batafsil'}</span>
                        <svg className="w-3 h-3 transform group-hover/link:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                    </Link>
                </div>
            </div>
        </div>
    );
};

const NewsDetail = () => {
    const [newsItem, setNewsItem] = useState(null);
    const [otherNews, setOtherNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { id } = useParams();
    const { currentLanguage } = useLanguage();
    const { t } = useTranslation();

    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();

        const loadNewsItem = async () => {
            try {
                setLoading(true);
                setError('');
                
                if (!id || isNaN(parseInt(id))) {
                    throw new Error('Yangilik topilmadi');
                }

                const [newsData, allNews] = await Promise.all([
                    fetchNewsById(id, currentLanguage),
                    fetchNews(currentLanguage).catch(() => [])
                ]);
                
                if (!newsData) {
                    throw new Error('Yangilik topilmadi');
                }
                
                if (mounted) {
                    setNewsItem(newsData);
                    const filteredNews = (allNews || []).filter(n => n.id !== newsData.id);
                    setOtherNews(filteredNews.slice(0, 5));
                }
            } catch (err) {
                console.error('NewsDetail loading error:', err);
                if (mounted && !controller.signal.aborted) {
                    setError(err.message || 'Yangilik topilmadi');
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        loadNewsItem();

        return () => {
            mounted = false;
            controller.abort();
        };
    }, [id, currentLanguage]);

    const formatDate = useCallback((dateString) => {
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            return `${day}.${month}.${year}`;
        } catch (error) {
            console.error('Date formatting error:', error);
            return dateString;
        }
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
                <Container className="py-8 sm:py-12 lg:py-20">
                    <div className="max-w-4xl mx-auto">
                        {/* Breadcrumbs Skeleton */}
                        <div className="mb-6">
                            <div className="w-48 h-4 bg-slate-200 rounded animate-pulse"></div>
                        </div>
                        
                        {/* Title Skeleton */}
                        <div className="mb-6">
                            <div className="w-full h-10 bg-slate-200 rounded mb-4 animate-pulse"></div>
                            <div className="w-3/4 h-10 bg-slate-200 rounded animate-pulse"></div>
                        </div>
                        
                        {/* Meta info skeleton */}
                        <div className="flex items-center mb-8 space-x-4">
                            <div className="w-24 h-4 bg-slate-200 rounded animate-pulse"></div>
                            <div className="w-20 h-6 bg-slate-200 rounded-full animate-pulse"></div>
                        </div>
                        
                        {/* Image Skeleton */}
                        <div className="w-full h-96 bg-slate-200 rounded-2xl mb-8 animate-pulse"></div>
                        
                        {/* Content Skeleton */}
                        <div className="space-y-4">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <div key={i} className="w-full h-4 bg-slate-200 rounded animate-pulse"></div>
                            ))}
                            <div className="w-2/3 h-4 bg-slate-200 rounded animate-pulse"></div>
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
                <Container className="py-8 sm:py-12 lg:py-20">
                    <div className="text-center py-20">
                        <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-8 sm:p-12 max-w-lg mx-auto border border-red-200/50 shadow-xl">
                            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-red-700 mb-4">{t('common.error') || 'Xatolik yuz berdi'}</h3>
                            <p className="text-red-600 mb-8">{error}</p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Link 
                                    to="/news"
                                    className="inline-flex items-center justify-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                                >
                                    <span className="mr-2">{t('pages.news.backToNews') || 'Yangiliklarni ko\'rish'}</span>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                    </svg>
                                </Link>
                                <Link 
                                    to="/"
                                    className="inline-flex items-center justify-center bg-slate-600 hover:bg-slate-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                                >
                                    <span className="mr-2">{t('nav.home') || 'Bosh sahifa'}</span>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                    </svg>
                                </Link>
                            </div>
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    if (!newsItem) {
        return (
            <Container className="py-10">
                <div className="text-center py-20">
                    <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-12 max-w-lg mx-auto border border-slate-200/50 shadow-xl">
                        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-slate-800 mb-4">{t('pages.news.noNews') || 'Yangilik topilmadi'}</h3>
                        <p className="text-slate-600 mb-8">{t('pages.news.noNewsDescription') || 'Siz qidirayotgan yangilik mavjud emas'}</p>
                        <div className="flex gap-4 justify-center">
                            <Link 
                                to="/news"
                                className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                            >
                                <span className="mr-2">{t('pages.news.backToNews') || 'Yangiliklarni ko\'rish'}</span>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                            </Link>
                        </div>
                    </div>
                </div>
            </Container>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
            <Container className="py-8 sm:py-12 lg:py-20">
                <article className="max-w-4xl mx-auto">
                    {/* Breadcrumbs */}
                    <nav className="mb-6 sm:mb-8 text-sm">
                        <div className="flex items-center space-x-2 text-slate-500">
                            <Link to="/" className="hover:text-red-600 transition-colors duration-200 flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                </svg>
                                {t('nav.home')}
                            </Link>
                            <svg className="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <Link to="/news" className="hover:text-red-600 transition-colors duration-200">{t('nav.news')}</Link>
                            <svg className="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <span className="text-slate-700 font-medium">
                                {newsItem.title && newsItem.title.length > 30 
                                    ? `${newsItem.title.substring(0, 30)}...` 
                                    : newsItem.title
                                }
                            </span>
                        </div>
                    </nav>

                    {/* News Header */}
                    <header className="mb-8 sm:mb-12">
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 sm:mb-6 leading-tight">
                            {newsItem.title || t('pages.news.noTitle') || 'Sarlavha yo\'q'}
                        </h1>
                        
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 text-sm text-slate-600">
                            <div className="flex items-center space-x-2">
                                <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span className="font-medium">{formatDate(newsItem.created_at)}</span>
                            </div>
                            
                            {newsItem.is_featured && (
                                <div className="flex items-center space-x-2">
                                    <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-md">
                                        ⭐ {t('common.featured') || 'Asosiy'}
                                    </span>
                                </div>
                            )}
                            
                            <div className="flex items-center space-x-2">
                                <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>📖 2-3 daqiqa o'qish</span>
                            </div>
                        </div>
                    </header>

                    {/* Hero Image */}
                    {newsItem.image && (
                        <div className="mb-8 sm:mb-12 rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl bg-white/10 border border-white/20">
                            <img 
                                src={newsItem.image}
                                alt={newsItem.title}
                                className="w-full h-64 sm:h-80 lg:h-96 object-cover"
                            />
                        </div>
                    )}

                    {/* Content */}
                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl lg:rounded-3xl p-6 sm:p-8 lg:p-12 shadow-xl border border-white/30 mb-8 sm:mb-12">
                        <div className="prose prose-slate prose-lg sm:prose-xl max-w-none">
                            {newsItem.content ? (
                                <div 
                                    className="text-slate-700 leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: newsItem.content }} 
                                />
                            ) : (
                                <div className="text-center py-12">
                                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <p className="text-slate-500 italic text-lg">
                                        {t('pages.news.noContent') || 'Yangilik matni mavjud emas'}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Other News Section */}
                    {otherNews.length > 0 && (
                        <div className="mt-16">
                            <div className="bg-gradient-to-br from-red-50/90 via-white/80 to-red-50/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-red-200/30 p-8 lg:p-12">
                                <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-8">
                                    {t('pages.news.otherNews') || 'Diğer Haberler'}
                                </h2>
                                {/* Responsive Grid Layout for 5 News - Optimized for better display */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6 lg:gap-8">
                                    {otherNews.map((item, index) => (
                                        <NewsCard 
                                            key={item.id} 
                                            item={item} 
                                            formatDate={formatDate}
                                            index={index}
                                            t={t}
                                        />
                                    ))}
                                </div>
                                {/* Show More Button */}
                                <div className="text-center mt-8">
                                    <Link 
                                        to="/news" 
                                        className="inline-flex items-center bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold py-3 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                                    >
                                        <span className="mr-2">{t('pages.news.allNews') || 'Barcha Yangiliklar'}</span>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                        </svg>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-16">
                        <Link 
                            to="/news"
                            className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            {t('pages.news.backToNews') || 'Yangiliklarni ko\'rish'}
                        </Link>
                        
                        <Link 
                            to="/"
                            className="inline-flex items-center bg-slate-600 hover:bg-slate-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                            </svg>
                            {t('nav.home') || 'Bosh sahifa'}
                        </Link>
                    </div>
                </article>
            </Container>
            
            <style jsx>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .line-clamp-2 {
                    overflow: hidden;
                    display: -webkit-box;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: 2;
                }
                
                .line-clamp-3 {
                    overflow: hidden;
                    display: -webkit-box;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: 3;
                }
            `}</style>
        </div>
    );
};

export default NewsDetail;
